import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

type CacheStatus = 'network' | 'cache' | null;

interface OfflineSnapshotNoticeProps {
  menuStatus: CacheStatus;
  menuCachedAt: number | null;
  tablesStatus: CacheStatus;
  tablesCachedAt: number | null;
}

function formatRelativeTime(value: number): string {
  const delta = Date.now() - value;
  if (delta <= 0) {
    return 'just now';
  }
  const seconds = Math.floor(delta / 1000);
  if (seconds < 60) {
    return String(seconds) + 's ago';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return String(minutes) + 'm ago';
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return String(hours) + 'h ago';
  }
  const days = Math.floor(hours / 24);
  return String(days) + 'd ago';
}

export function OfflineSnapshotNotice({
  menuStatus,
  menuCachedAt,
  tablesStatus,
  tablesCachedAt,
}: OfflineSnapshotNoticeProps): JSX.Element | null {
  const offlineActive = menuStatus === 'cache' || tablesStatus === 'cache';
  if (!offlineActive) {
    return null;
  }

  const sources: string[] = [];
  if (menuStatus === 'cache') {
    sources.push('menu');
  }
  if (tablesStatus === 'cache') {
    sources.push('table list');
  }
  const sourceLabel = sources.join(' and ');

  const timestamps: number[] = [];
  if (menuStatus === 'cache' && typeof menuCachedAt === 'number') {
    timestamps.push(menuCachedAt);
  }
  if (tablesStatus === 'cache' && typeof tablesCachedAt === 'number') {
    timestamps.push(tablesCachedAt);
  }
  const offlineCachedAt = timestamps.length ? Math.max(...timestamps) : null;
  const offlineAgeLabel = offlineCachedAt !== null ? formatRelativeTime(offlineCachedAt) : null;

  return (
    <Alert variant="default" className="mb-6 bg-muted/50">
      <AlertTitle>Offline snapshot in use</AlertTitle>
      <AlertDescription>
        We are showing cached {sourceLabel} data while offline.{' '}
        {offlineAgeLabel ? 'Last updated ' + offlineAgeLabel + '.' : 'Reconnect to refresh live data.'}
      </AlertDescription>
    </Alert>
  );
}

export default OfflineSnapshotNotice;
